import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IndustryService } from 'services'

import { toasts } from 'utils/toasts'

import { IndustryType } from 'types/Industry.types'

import { StatusBox } from 'components/StatusBox'
import { IndustryDropdown } from './IndustryDropdown'
import { TableNotFoundHint } from 'components/TableNotFoundHint'
import { ModalDeleteIndustry } from 'components/Industry/ModalDeleteIndustry/ModalDeleteIndustry'

import * as S from './styled'

export type IndustryTableProps = {
  rows: IndustryType[]
  isDeleted: (_) => void
}

export function IndustryTable({ rows, isDeleted }: IndustryTableProps) {
  const { t } = useTranslation()
  const industryService = new IndustryService()

  const [_deleteIndustry, _setDeleteIndustry] = useState({
    cnpj: null,
    id: null
  })

  function onExit() {
    _setDeleteIndustry({ cnpj: null, id: null })
  }

  async function onDelete(id) {
    try {
      const { success } = await industryService.deleteIndustry({ id })

      if (success) {
        isDeleted(true)
        toasts.deleted()
        onExit()
      } else {
        toasts.generalFail()
      }
    } catch (error) {
      console.log('DeleteIndustry-error', error)
    } finally {
      isDeleted(false)
    }
  }

  return (
    <S.Wrapper data-testid="IndustryTable">
      {_deleteIndustry.id ? (
        <ModalDeleteIndustry
          cnpj={_deleteIndustry.cnpj}
          onExit={onExit}
          onRemove={() => onDelete(_deleteIndustry.id)}
        />
      ) : null}
      <S.Teste>
        <S.Table id="table-fixed">
          <tbody>
            <S.Line id="header">
              <S.Header>{t('generalIndustriesPage:table:status')}</S.Header>
            </S.Line>
            {rows.map(({ id, status }) => (
              <S.Line id="values" key={id}>
                <S.Column className="center">
                  <StatusBox variant={status ? 'active' : 'inactive'}>
                    {status ? 'ativo' : 'inativo'}
                  </StatusBox>
                </S.Column>
              </S.Line>
            ))}
          </tbody>
        </S.Table>
        <S.ScrollWrapper>
          <S.Table id="table-scrolled">
            <tbody>
              <S.Line id="header">
                <S.Header>
                  {t('generalIndustriesPage:table:corporateName')}
                </S.Header>
                <S.Header>
                  {t('generalIndustriesPage:table:fantasyName')}
                </S.Header>
                <S.Header>{t('generalIndustriesPage:table:type')}</S.Header>
                <S.Header className="right">
                  {t('generalIndustriesPage:table:CNPJ')}
                </S.Header>
                <S.Header className="right">
                  {t('generalIndustriesPage:table:stateRegistration')}
                </S.Header>
                <S.Header className="right">
                  {t('generalIndustriesPage:table:groups')}
                </S.Header>
                <S.Header className="right">
                  {t('generalIndustriesPage:table:latitude')}
                </S.Header>
                <S.Header className="right">
                  {t('generalIndustriesPage:table:longitude')}
                </S.Header>
              </S.Line>
              {rows.map(each => (
                <S.Line id="values" key={each.id}>
                  <S.Column>{each.name}</S.Column>
                  <S.Column>{each.fancyName}</S.Column>
                  <S.Column>
                    {each.companyProfiles
                      .map(profile => profile.description)
                      .join(', ')}
                  </S.Column>
                  <S.Column className="right">{each.cnpj}</S.Column>
                  <S.Column className="right">{each.ie}</S.Column>
                  <S.Column className="right">{each.total}</S.Column>
                  <S.Column className="right">{each.lat || '-'}</S.Column>
                  <S.Column className="right">{each.lng || '-'}</S.Column>
                </S.Line>
              ))}
            </tbody>
          </S.Table>
        </S.ScrollWrapper>
        <S.Table id="table-fixed">
          <tbody>
            <S.Line id="header">
              <S.Header>{t('generalIndustriesPage:table:actions')}</S.Header>
            </S.Line>
            {Boolean(rows && rows.length) &&
              rows.map(({ cnpj, id }) => (
                <S.Line key={id}>
                  <S.Column id="actions-column">
                    <IndustryDropdown
                      id={id}
                      setDeleteIndustry={() => _setDeleteIndustry({ cnpj, id })}
                    />
                  </S.Column>
                </S.Line>
              ))}
          </tbody>
        </S.Table>
      </S.Teste>
      {!rows.length && <TableNotFoundHint to="/industria/novo" />}
    </S.Wrapper>
  )
}
